import moment from 'moment';
import React, { FC, useState, useEffect, useRef, ReactEventHandler } from 'react';
import Calendar from 'src/components/atoms/Calendar';
import RequiredLabel from 'src/components/atoms/label/Required';
import { currentTimeFormat } from 'src/utils/common';
import { dateCalendarFormatType } from 'src/utils/dateSet';
import './style.scss';

interface Props {
  status: string;
  value?: string;
  placeholder?: string;
  inpRef?: React.ForwardedRef<HTMLInputElement>;
  direction: string;
  label?: string;
  labelPosition?: string;
  styleDef?: React.CSSProperties;
  labelStyleDef?: React.CSSProperties;
  clickCalendarEvt?: (str: string, field?: any) => void;
  divId?: string;
  required?: string;
  defaultToday?: boolean;
  minDate?: string; // 'YYYYMMDD' - 선택/입력 가능한 최소일
  maxDate?: string; // 'YYYYMMDD' - 선택/입력 가능한 최대일
}

const InputDateBox: FC<Props> = ({
  label = '',
  status,
  placeholder = '',
  value = '',
  inpRef,
  direction,
  labelPosition,
  styleDef,
  labelStyleDef,
  clickCalendarEvt,
  divId,
  required,
  defaultToday = true,
  minDate,
  maxDate,
}) => {
  // Value
  if (value === '' && defaultToday) value = currentTimeFormat();
  const labelWidth = styleDef?.minWidth || '80px';
  const calendarRef = inpRef || useRef<HTMLInputElement | null>(null);
  // State
  const [calendarState, setCalendarState] = useState({ active: status, date: value });
  const dateValue = calendarState.date;
  // 입력을 다 마치지 않은 값(예: '2024.0')이나 1970년 미만 연도는
  // calendar 라이브러리가 CalendarException을 던지므로 완전한 날짜일 때만 유효 처리
  const isCompleteDate =
    /^\d{4}\.\d{2}\.\d{2}$/.test(dateValue) &&
    moment(dateValue, 'YYYY.MM.DD', true).isValid() &&
    Number(dateValue.slice(0, 4)) >= 1970;
  // 입력값이 비어있거나 미완성이어도 달력은 최소 선택일(또는 오늘) 기준으로 정상 노출
  const calendarViewDate = isCompleteDate
    ? dateValue
    : moment(minDate || undefined, minDate ? 'YYYYMMDD' : undefined).format('YYYY.MM.DD');

  // yyyy.mm.dd 형태로 입력값 자동 포맷
  const inpChangeEvt = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = dateCalendarFormatType(e.target.value);
    setCalendarState({ ...calendarState, date: formatted });
    if (clickCalendarEvt) clickCalendarEvt(formatted, divId);
  };

  useEffect(() => {
    if (value !== '') setCalendarState({ ...calendarState, date: value });
  }, [value]);

  // Event
  const calendarClickEvt = (date: string, target: HTMLElement) => {
    if (target.className !== 'ri-close-fill') {
      if (date !== '') {
        const setDate = moment(date).format('YYYY.MM.DD');
        setCalendarState({ date: setDate, active: '' });
        if (clickCalendarEvt) clickCalendarEvt(setDate, divId);
      } else {
        setCalendarState({ ...calendarState, active: '' });
      }
    }
  };
  const viewCalendarDate = (e: React.MouseEvent) => {
    calendarState.active === '_active'
      ? setCalendarState({ ...calendarState, active: '' })
      : setCalendarState({ ...calendarState, active: '_active' });
  };

  const statusBtn = (status: string) => {
    if (status === '_disable') {
      return (
        <div className="_btn_div">
          <button className="_btn _disable">
            <i className="ri-calendar-2-line" />
          </button>
        </div>
      );
    }
    return (
      <div className="_btn_div">
        {calendarState.active === '_active' ? (
          <button className="_btn" onClick={e => viewCalendarDate(e)}>
            <i className="ri-close-fill" />
          </button>
        ) : (
          <button className="_btn" onClick={e => viewCalendarDate(e)}>
            <i className="ri-calendar-2-line" />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className={['_title_input_date_box', calendarState.active, labelPosition].join(' ')}>
      {label !== '' ? (
        <div className={['_label_div', labelPosition].join(' ')} style={labelStyleDef}>
          <div className="_text">{label}</div>
        </div>
      ) : (
        ''
      )}
      <div className="_input_div" style={styleDef}>
        {required === 'Y' ? <RequiredLabel styleDef={{ marginTop: '2px' }} /> : ''}
        <div className={['_input', status].join(' ')}>
          <input
            type="text"
            placeholder={placeholder}
            value={dateValue || ''}
            ref={calendarRef}
            maxLength={10}
            onChange={inpChangeEvt}
            disabled={status === '_disable'}
          />
        </div>
        <div className="_abs_div">
          <Calendar
            clickEvt={calendarClickEvt}
            active={calendarState.active}
            direction={direction}
            setDate={calendarViewDate}
            minDate={minDate}
            maxDate={maxDate}
          />
        </div>
        {statusBtn(status)}
      </div>
    </div>
  );
};

export default InputDateBox;
